<template>
  <div class="h-screen bg-secondary overflow-auto">
    <div class="py-4"></div>
    <div class="text-white sticky top-1">
      <div class="mobile-status-padding"></div>

      <div class="text-center text-3xl font-bold">Classements</div>
    </div>
    <div class="px-6 py-2 text-white/60 text-center text-lg">
      Liste des meilleurs dépôts. Les 3 premiers gagnent des bonus.
    </div>
    <div class="px-6 pb-8 text-white">
      <div class="py-3"></div>
      <div class="bg-black/10 grid grid-cols-2 rounded-3xl p-2 font-bold">
        <div
          class="rounded-3xl p-4 flex items-center justify-center cursor-pointer transition-colors"
          :class="{ 'bg-white/20': currentTab === 'weekly' }"
          @click="switchTab('weekly')"
        >
          Cette semaine
        </div>
        <div
          class="rounded-3xl p-4 flex items-center justify-center cursor-pointer transition-colors"
          :class="{ 'bg-white/20': currentTab === 'alltime' }"
          @click="switchTab('alltime')"
        >
          Tout
        </div>
      </div>
      <div class="bg-[#FFAF6C] rounded-3xl flex mt-4 p-4 items-center">
        <div
          class="bg-black/20 flex items-center justify-center text-xl font-bold p-4 rounded-2xl w-16 min-w-16 h-16"
        >
          #{{ userPosition }}
        </div>
        <div class="w-full p-3">
          <div class="font-semibold">{{ getUserRankMessage() }}</div>
          <div class="text-sm opacity-90 mt-1">
            Score: {{ formatScore(userScore) }} - Bonus:
            {{ formatBonus(userBonus) }},
          </div>
        </div>
      </div>
      <div class="flex justify-end mt-4 -mb-8">
        <div class="rounded-2xl px-3 py-2 text-sm bg-black/10">
          {{ timeRemaining }}
        </div>
      </div>
    </div>
    <div class="z-[1] sticky top-10">
      <div class="mobile-status-padding"></div>

      <div class="grid grid-cols-3 px-6 overflow-hidden z-[1] text-white">
        <div
          v-for="player in topThree"
          :key="player.position"
          class="flex flex-col items-center"
          :class="{
            'order-2 z-[3]': player.position === 1,
            'order-1 z-[1]': player.position === 2,
            'order-3 z-[1]': player.position === 3
          }"
        >
          <div class="py-[1.5rem]" v-if="player.position >= 2"></div>
          <div class="py-[1rem]" v-if="player.position === 3"></div>

          <!-- Avatar -->
          <div class="w-[60%] relative">
            <div class="py-[50%]"></div>
            <ImgComponent
              avatar
              :src="player.avatar"
              class="absolute w-full h-full rounded-full top-0 left-0 bg-white/30"
              alt=""
            />
          </div>

          <!-- Medal -->
          <div class="text-4xl mt-2">{{ player.medal }}</div>

          <!-- Name -->
          <div class="mt-2 font-semibold text-center text-sm">
            {{ player.name }}
          </div>

          <!-- Score -->
          <div class="mt-3 rounded-xl bg-white/25 px-4 py-2 text-xs">
            {{ formatScore(player.score) }}
          </div>

          <div class="relative w-[110%] mt-2">
            <div
              class="w-full absolute flex justify-center"
              :class="{ '-scale-x-100': player.position == 3 }"
              :style="{
                'mask-image': `url(${
                  player.position == 1
                    ? ImgUtils.normalizeToBase64(podiumCenterImage)
                    : ImgUtils.normalizeToBase64(podiumLeftImage)
                })`,
                'mask-size': '100%'
              }"
            >
              <img
                :src="podiumCenterImage"
                v-if="player.position == 1"
                alt=""
              />
              <img :src="podiumLeftImage" v-else alt="" />
              <div
                :class="{
                  'bg-[#f8d330]': player.position == 1,
                  'bg-[#DDDDDD]': player.position == 2,
                  'bg-[#B78B4E]': player.position == 3
                }"
                class="w-full h-full absolute top-0 left-0 mix-blend-multiply"
              ></div>
            </div>
            <div
              class="absolute text-6xl drop-shadow-xl text-white font-black w-full text-center"
            >
              <div class="py-[1rem]"></div>
              {{ player.position }}
            </div>
          </div>
          <div class="h-[15vh]"></div>
        </div>
      </div>
    </div>
    <!-- Leaderboard List -->
    <div class="-mt-[5vh] relative z-[1]">
      <div class="relative w-full">
        <ColorImg :src="cardHeadForm" class="w-full" alt="" />
      </div>
      <div class="-mt-8 rounded-t-3xl h-full pb-32 relative px-5">
        <!-- Player Cards -->
        <div
          v-for="player in allPlayers.filter((p) => p.position > 3)"
          :key="player.position"
          class="rounded-3xl p-6 grid grid-cols-11 items-center gap-3 mb-4"
          :class="{
            'bg-white': player.name !== 'Vous',
            'bg-gradient-to-r from-yellow-100 to-orange-100 border-2 border-orange-300 shadow-lg':
              player.name === 'Vous'
          }"
        >
          <!-- Position -->
          <div class="col-span-1 flex items-center justify-center">
            <div
              class="border w-8 h-8 min-w-8 flex justify-center items-center text-lg rounded-full"
              :class="{
                'border-current text-gray-600': player.name !== 'Vous',
                'bg-orange-500 text-white border-orange-500 font-bold':
                  player.name === 'Vous'
              }"
            >
              {{ player.position }}
            </div>
          </div>

          <!-- Avatar -->
          <div class="col-span-2">
            <ImgComponent
              :src="player.avatar"
              avatar
              class="rounded-full w-12 h-12"
              alt=""
            />
          </div>

          <!-- Player Info -->
          <div class="col-span-8">
            <div
              class="font-bold text-xl"
              :class="{
                'text-gray-900': player.name !== 'Vous',
                'text-orange-600 flex items-center': player.name === 'Vous'
              }"
            >
              {{ player.name }}
              <span
                v-if="player.name === 'Vous'"
                class="ml-2 text-xs bg-orange-500 text-white px-2 py-1 rounded-full"
                >VOUS</span
              >
            </div>
            <div
              class="text-lg"
              :class="{
                'text-gray-600': player.name !== 'Vous',
                'text-orange-700 font-semibold': player.name === 'Vous'
              }"
            >
              {{ formatScore(player.score) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import podiumCenterImage from '@/assets/images/leaderboard/podium-center.svg';
import podiumLeftImage from '@/assets/images/leaderboard/podium-left.svg';
import cardHeadForm from '@/assets/images/leaderboard/cardHeadForm.svg';
import { ImgUtils } from '@/app/ui/core/img/imgUtils.js';

// États réactifs
const currentTab = ref('weekly');
const isLoading = ref(false);
const userPosition = ref(4);
const userScore = ref(150000);
const userBonus = ref(13000);
const timeRemaining = ref('0j 30h 0min');

// Données du tableau de scores hebdomadaire
const weeklyData = ref({
  topThree: [
    {
      position: 1,
      name: 'Ale********',
      score: 500000,
      avatar: 'leader1',
      medal: '🥇'
    },
    {
      position: 2,
      name: 'Mar********',
      score: 350000,
      avatar: 'leader2',
      medal: '🥈'
    },
    {
      position: 3,
      name: 'Sop********',
      score: 250000,
      avatar: 'leader3',
      medal: '🥉'
    }
  ],
  otherPlayers: [
    {
      position: 5,
      name: 'Pie********',
      score: 140000,
      avatar: 'pierre'
    },
    {
      position: 6,
      name: 'Ali********',
      score: 120000,
      avatar: 'alice'
    },
    {
      position: 7,
      name: 'Dav********',
      score: 100000,
      avatar: 'david'
    },
    {
      position: 8,
      name: 'Emm********',
      score: 85000,
      avatar: 'emma'
    },
    {
      position: 9,
      name: 'Luc********',
      score: 70000,
      avatar: 'lucas'
    },
    {
      position: 10,
      name: 'Jul********',
      score: 55000,
      avatar: 'julie'
    }
  ]
});

// Données du tableau de scores tous temps
const allTimeData = ref({
  topThree: [
    {
      position: 1,
      name: 'Leg********',
      score: 2500000,
      avatar: 'champ',
      medal: '👑'
    },
    {
      position: 2,
      name: 'Mas********',
      score: 1800000,
      avatar: 'master',
      medal: '🥈'
    },
    {
      position: 3,
      name: 'Exp********',
      score: 1200000,
      avatar: 'expert',
      medal: '🥉'
    }
  ],
  otherPlayers: [
    {
      position: 5,
      name: 'Pro********',
      score: 800000,
      avatar: 'pro'
    },
    {
      position: 6,
      name: 'Adv********',
      score: 650000,
      avatar: 'advanced'
    },
    {
      position: 7,
      name: 'Reg********',
      score: 450000,
      avatar: 'regular'
    },
    {
      position: 8,
      name: 'Cas********',
      score: 320000,
      avatar: 'casual'
    },
    {
      position: 9,
      name: 'Beg********',
      score: 180000,
      avatar: 'beginner'
    },
    {
      position: 10,
      name: 'New********',
      score: 95000,
      avatar: 'newcomer'
    }
  ]
});

// Computed properties pour les données actuelles
const currentData = computed(() => {
  return currentTab.value === 'weekly' ? weeklyData.value : allTimeData.value;
});

const topThree = computed(() => currentData.value.topThree);

const allPlayers = computed(() => {
  const players = [...topThree.value, ...currentData.value.otherPlayers];

  // Ajouter l'utilisateur s'il n'est pas dans le top 3
  if (userPosition.value > 3) {
    const userPlayer = {
      position: userPosition.value,
      name: 'Vou********',
      score: userScore.value,
      avatar: 'currentuser'
    };

    // Vérifier si l'utilisateur n'existe pas déjà
    if (!players.find((p) => p.name === 'Vous')) {
      players.push(userPlayer);
    }
  }

  return players.sort((a, b) => a.position - b.position);
});

// Méthodes
const formatScore = (score) => {
  return `${parseInt(score).toLocaleString('fr-FR')} FCFA`;
};
// Méthodes
const formatBonus = (bonus) => {
  return `${parseInt(bonus).toLocaleString('fr-FR')} FCFA`;
};

const getUserRankMessage = () => {
  const percentage = Math.round((1 - userPosition.value / 100) * 100);
  return `Vous faites mieux que ${percentage}% des autres joueurs!`;
};

const switchTab = (tab) => {
  if (tab !== currentTab.value && !isLoading.value) {
    isLoading.value = true;
    currentTab.value = tab;

    // Simuler un chargement
    setTimeout(() => {
      isLoading.value = false;
    }, 300);
  }
};

const updateTimeRemaining = () => {
  const now = new Date();
  const endOfWeek = new Date(now);
  endOfWeek.setDate(now.getDate() + (7 - now.getDay()));
  endOfWeek.setHours(23, 59, 59, 999);

  const diff = endOfWeek - now;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  timeRemaining.value = `${days}j ${hours}h ${minutes}min`;
};

// Simuler la récupération des données depuis une API
const fetchLeaderboardData = async (type) => {
  try {
    isLoading.value = true;

    // Simuler un appel API
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Ici vous feriez un véritable appel API
    // const response = await fetch(`/api/leaderboard?type=${type}`)
    // const data = await response.json()

    // Pour l'instant, on utilise les données mockées
  } catch (error) {
    console.error('Erreur lors de la récupération des données:', error);
  } finally {
    isLoading.value = false;
  }
};

// Watchers
watch(currentTab, (newTab) => {
  fetchLeaderboardData(newTab);
});

// Lifecycle hooks
onMounted(() => {
  fetchLeaderboardData(currentTab.value);
  updateTimeRemaining();

  // Mettre à jour le temps restant toutes les minutes
  const interval = setInterval(updateTimeRemaining, 60000);
});
</script>
